/**
 * Circular progress ring showing 4-week training consistency score.
 * Score = (completed / planned) * 100, capped at 100%.
 */

import { useTranslation } from 'react-i18next';
import { GlassCard } from '@/components/ui/GlassCard';
import { CardContent } from '@/components/ui/layout/card';
import { useAuthenticatedQuery } from '@/hooks/useAuthenticatedQuery';
import { fetchConsistencyScore, type ConsistencyData } from '@/lib/analyticsApi';

const RING_SIZE = 64;
const STROKE_WIDTH = 5;
const RADIUS = (RING_SIZE - STROKE_WIDTH) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

function ScoreRing({ score }: { score: number }) {
  const offset = CIRCUMFERENCE - (score / 100) * CIRCUMFERENCE;
  const color = score >= 80 ? '#00FFC8' : score >= 50 ? '#FFB43C' : '#FF6B6B';

  return (
    <svg width={RING_SIZE} height={RING_SIZE} className="shrink-0 -rotate-90">
      <circle
        cx={RING_SIZE / 2}
        cy={RING_SIZE / 2}
        r={RADIUS}
        fill="none"
        stroke="rgba(255,255,255,0.06)"
        strokeWidth={STROKE_WIDTH}
      />
      <circle
        cx={RING_SIZE / 2}
        cy={RING_SIZE / 2}
        r={RADIUS}
        fill="none"
        stroke={color}
        strokeWidth={STROKE_WIDTH}
        strokeDasharray={CIRCUMFERENCE}
        strokeDashoffset={offset}
        strokeLinecap="round"
        className="transition-all duration-700 ease-out"
      />
    </svg>
  );
}

export function ConsistencyRing() {
  const { t } = useTranslation();

  const { data, isLoading } = useAuthenticatedQuery<ConsistencyData>({
    queryKey: ['analytics', 'consistency'],
    queryFn: fetchConsistencyScore,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });

  if (isLoading || !data) return null;

  // Don't show if no planned sessions
  if (data.planned_sessions === 0) return null;

  return (
    <GlassCard className="border-white/[0.06]">
      <CardContent className="p-4 flex items-center gap-4">
        <div className="relative">
          <ScoreRing score={data.score} />
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-sm font-mono font-semibold text-foreground">
              {Math.round(data.score)}
            </span>
          </div>
        </div>

        <div className="min-w-0">
          <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground block">
            {t('dashboard.consistency.title', { defaultValue: 'Consistency' })}
          </span>
          <span className="text-xs text-muted-foreground/70 font-mono">
            {t('dashboard.consistency.detail', {
              defaultValue: '{{completed}}/{{planned}} sessions ({{weeks}}w)',
              completed: data.completed_sessions,
              planned: data.planned_sessions,
              weeks: data.window_weeks,
            })}
          </span>
        </div>
      </CardContent>
    </GlassCard>
  );
}
